import React, { useState, useEffect, useRef } from 'react';
import { listProspects, listProspectCounts } from '../../services/prospectApi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Toast } from 'primereact/toast';

const avatarColors = [
  '#1a7a3a', '#0d6efd', '#6f42c1', '#d97706',
  '#0891b2', '#be185d', '#059669', '#7c3aed',
];

function getAvatarColor(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

function getInitials(name: string) {
  return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

function formatDate(val: string): string {
  if (!val) return '';
  const d = new Date(val + 'T00:00:00');
  if (isNaN(d.getTime())) return val;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}


type FilterTab = 'all' | 'Pending' | 'Approved' | 'Rejected' | 'docsStarted' | 'readyToSubmit';

const tabs: { key: FilterTab; label: string; color: string }[] = [
  { key: 'all',           label: 'All Quotes',     color: '#1a7a3a' },
  { key: 'Pending',       label: 'Pending',         color: '#d97706' },
  { key: 'Approved',      label: 'Approved',        color: '#059669' },
  { key: 'Rejected',      label: 'Rejected',        color: '#dc2626' },
  { key: 'docsStarted',   label: 'Docs Started',    color: '#0891b2' },
  { key: 'readyToSubmit', label: 'Ready to Submit', color: '#7c3aed' },
];

const ITEMS_PER_PAGE = 9;
const DATE_FILTER_KEY = 'qh_date_filter';

function getInitialDateRange(): { from: string; to: string } {
  const today    = new Date().toISOString().slice(0, 10);
  const sevenAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  try {
    const saved = JSON.parse(localStorage.getItem(DATE_FILTER_KEY) || '{}');
    return { from: saved.from || sevenAgo, to: saved.to || today };
  } catch {
    return { from: sevenAgo, to: today };
  }
}

const QuoteHistory: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useRef<Toast>(null);

  const [activeTab,       setActiveTab]       = useState<FilterTab>('all');
  const [searchQuery,     setSearchQuery]     = useState('');
  const [currentPage,     setCurrentPage]     = useState(1);
  const countedIds = useRef<Set<string>>(new Set());
  const [quotes,          setQuotes]          = useState<any[]>([]);
  const [isLoading,       setIsLoading]       = useState(true);
  const [fetchError,      setFetchError]      = useState<string | null>(null);
  const [docStatusMap,    setDocStatusMap]    = useState<Record<string, { hasDocuments: boolean; canSubmit: boolean; qualityScore: number }>>({});
  const [docStatusLoading, setDocStatusLoading] = useState(false);

  const dateFromRef = useRef<HTMLInputElement>(null);
  const dateToRef   = useRef<HTMLInputElement>(null);

  // Resolved synchronously on first render: URL params → localStorage → 7-day default
  const [initialDates] = useState(getInitialDateRange);
  const dateFrom = searchParams.get('from') || initialDates.from;
  const dateTo   = searchParams.get('to')   || initialDates.to;

  // Sync the resolved dates into the URL on mount (same values → no refetch triggered)
  useEffect(() => {
    if (searchParams.get('from') && searchParams.get('to')) return;
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (!p.get('from')) p.set('from', dateFrom);
      if (!p.get('to'))   p.set('to',   dateTo);
      return p;
    }, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep localStorage in sync with the current date filter
  useEffect(() => {
    localStorage.setItem(DATE_FILTER_KEY, JSON.stringify({ from: dateFrom, to: dateTo }));
  }, [dateFrom, dateTo]);

  // Fetch quotes from API — re-runs when date filter changes.
  // API defaults to last 7 days when no dates are provided.
  useEffect(() => {
    const token = localStorage.getItem('igtoken');
    const params: Record<string, string> = {};
    if (dateFrom) params.fromDate = dateFrom;
    if (dateTo)   params.toDate   = dateTo;

    setIsLoading(true);
    axios({
      method: 'get',
      url: `${process.env.REACT_APP_TRUCKING_API_Url}/api/account/quotelist`,
      headers: { Authorization: `Bearer ${token}` },
      params,
    })
      .then((res: any) => {
        const mapped = (res.data || []).map((item: any) => ({
          id:           item.transactionId,
          quoteId:      item.quoteId ?? '',
          company:      item.companyName || '',
          date:         item.effectiveDate || '',
          createdDate:  item.createdDate || '',
          vehicleCount: item.vehicleCount ?? 0,
          driverCount:  item.driverCount ?? 0,
          status:       item.status,
          contact:      item.insuredName || '',
        }));
        // No MTM submissions - this agency's history is its prospects.
        if (mapped.length === 0) {
          listProspects()
            .then((prospects) => {
              setQuotes(prospects.map((p) => ({
              id:           p.insuredId ?? '',
              quoteId:      p.dotNumber ?? '',
              company:      p.name,
              date:         '',
              createdDate:  p.created ?? '',
              vehicleCount: null,
              driverCount:  null,
              status:       'Prospect',
              contact:      p.name,
              isProspect:   true,
              })));
            })
            .catch(() => setQuotes([]));   // empty is a valid state
          return;
        }
        setQuotes(mapped);
      })
      .catch((error: any) => {
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate('/login');
        } else {
          setFetchError('Failed to load quotes. Please try again.');
          toast.current?.show({
            severity: 'error', summary: 'Error',
            detail: error.response?.data?.message
              ? `Error ${error.response.status}: ${error.response.data.message}`
              : 'Failed to load quotes. Please try again.',
            life: 3000,
          });
        }
      })
      .finally(() => setIsLoading(false));
  }, [navigate, dateFrom, dateTo]);

  // Fetch document statuses for Docs Started / Ready to Submit counts.
  // Runs after every quote list refresh so counts stay in sync with the date filter.
  useEffect(() => {
    if (quotes.length === 0) {
      setDocStatusMap({});
      return;
    }
    const token    = localStorage.getItem('igtoken');
    const quoteIds = quotes.map((q: any) => q.id).filter(Boolean);
    if (quoteIds.length === 0) return;

    setDocStatusLoading(true);
    axios({
      method: 'post',
      url: `${process.env.REACT_APP_TRUCKING_PORTAL_AI_API_Url}/ai/submission-status/batch`,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      data: { quoteIds },
    })
      .then((batchRes: any) => {
        const raw = batchRes.data ?? {};
        const normalized: Record<string, any> = {};
        for (const [k, v] of Object.entries(raw)) normalized[k.toUpperCase()] = v;
        setDocStatusMap(normalized);
      })
      .catch(() => {})
      .finally(() => setDocStatusLoading(false));
  }, [quotes]);

  const docStatus = (id: string) => docStatusMap[(id || '').toUpperCase()];

  const counts: Record<FilterTab, number> = {
    all:           quotes.length,
    Pending:       quotes.filter((q: any) => q.status === 'Pending').length,
    Approved:      quotes.filter((q: any) => q.status === 'Approved').length,
    Rejected:      quotes.filter((q: any) => q.status === 'Rejected').length,
    docsStarted:   quotes.filter((q: any) => docStatus(q.id)?.hasDocuments).length,
    readyToSubmit: quotes.filter((q: any) => docStatus(q.id)?.canSubmit).length,
  };

  const q_lower = searchQuery.toLowerCase();
  const filtered = quotes.filter((q: any) => {
    let matchTab: boolean;
    if (activeTab === 'all')                matchTab = true;
    else if (activeTab === 'docsStarted')   matchTab = !!docStatus(q.id)?.hasDocuments;
    else if (activeTab === 'readyToSubmit') matchTab = !!docStatus(q.id)?.canSubmit;
    else                                    matchTab = q.status === activeTab;

    const matchSearch = !q_lower
      ? true
      : (q.contact || '').toLowerCase().includes(q_lower) ||
        (q.company  || '').toLowerCase().includes(q_lower) ||
        (q.quoteId  || '').toLowerCase().includes(q_lower);

    return matchTab && matchSearch;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const page       = Math.min(currentPage, totalPages);
  const paginated  = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  /* Prospect counts cost two NowCerts calls each, so only the visible page is
     fetched, and each row only once. */
  useEffect(() => {
    const ids = paginated
      .filter((q: any) => q.isProspect && q.vehicleCount === null && q.id)
      .map((q: any) => q.id)
      .filter((id: string) => !countedIds.current.has(id));
    if (ids.length === 0) return;

    ids.forEach((id: string) => countedIds.current.add(id));
    listProspectCounts(ids)
      .then((counts) => setQuotes((rows: any[]) => rows.map((r) => counts[r.id]
        ? { ...r, vehicleCount: counts[r.id].vehicles, driverCount: counts[r.id].drivers }
        : r)))
      .catch(() => undefined);   // counts are supplementary
  }, [paginated]);

  const handleTabChange = (tab: FilterTab) => { setActiveTab(tab); setCurrentPage(1); };
  const handleSearch    = (e: React.ChangeEvent<HTMLInputElement>) => { setSearchQuery(e.target.value); setCurrentPage(1); };

  const handleDateFrom = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      val ? p.set('from', val) : p.delete('from');
      if (val && dateTo && val > dateTo) p.delete('to');
      return p;
    }, { replace: true });
    setCurrentPage(1);
  };

  const handleDateTo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      val ? p.set('to', val) : p.delete('to');
      if (val && dateFrom && val < dateFrom) p.delete('from');
      return p;
    }, { replace: true });
    setCurrentPage(1);
  };

  const handleClearDates = () => {
    const today    = new Date().toISOString().slice(0, 10);
    const sevenAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    localStorage.setItem(DATE_FILTER_KEY, JSON.stringify({ from: sevenAgo, to: today }));
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.set('from', sevenAgo);
      p.set('to',   today);
      return p;
    }, { replace: true });
    setCurrentPage(1);
  };

  const badgeClass = (s: string) =>
    ({ Approved: 'badge badge--approved', Pending: 'badge badge--pending', Rejected: 'badge badge--rejected' } as Record<string, string>)[s];

  return (
    <div className="qh">
      <Toast ref={toast} position="top-right" />

      {/* ── Filter Tabs ── */}
      <div className="qh-tabs">
        {tabs.map(tab => (
          <button
            key={tab.key}
            className={`qh-tab${activeTab === tab.key ? ' qh-tab--active' : ''}`}
            style={{ '--tab-color': tab.color } as React.CSSProperties}
            onClick={() => handleTabChange(tab.key)}
          >
            <span className="qh-tab__count">
              {docStatusLoading && (tab.key === 'docsStarted' || tab.key === 'readyToSubmit')
                ? <span className="qh-tab__count-loading">…</span>
                : counts[tab.key]
              }
            </span>
            <span className="qh-tab__label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* ── Main Table Card ── */}
      <div className="qh-card">

        {/* Toolbar */}
        <div className="qh-toolbar">
          <div className="qh-toolbar__left">
            <div className="search-box">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                placeholder="Search by company or quote ID…"
                value={searchQuery}
                onChange={handleSearch}
              />
            </div>
            <span className="qh-count-chip">
              {filtered.length} result{filtered.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="qh-toolbar__right">
            {/* ── Date range filter ── */}
            <div className="qh-date-filter">
              <div className="qh-date-filter__icon">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <label className="qh-date-filter__half" onClick={(e) => { e.preventDefault(); dateFromRef.current?.showPicker?.(); }}>
                <input ref={dateFromRef} type="date" value={dateFrom} max={dateTo || undefined} onChange={handleDateFrom} />
                <div className="qh-date-filter__text">
                  <span className="qh-date-filter__text-label">From</span>
                  <span className={`qh-date-filter__text-value${!dateFrom ? ' qh-date-filter__text-value--empty' : ''}`}>
                    {dateFrom ? formatDate(dateFrom) : 'Start date'}
                  </span>
                </div>
              </label>
              <div className="qh-date-filter__divider" />
              <div className="qh-date-filter__arrow">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"/>
                  <polyline points="12 5 19 12 12 19"/>
                </svg>
              </div>
              <label className="qh-date-filter__half" onClick={(e) => { e.preventDefault(); dateToRef.current?.showPicker?.(); }}>
                <input ref={dateToRef} type="date" value={dateTo} min={dateFrom || undefined} onChange={handleDateTo} />
                <div className="qh-date-filter__text">
                  <span className="qh-date-filter__text-label">To</span>
                  <span className={`qh-date-filter__text-value${!dateTo ? ' qh-date-filter__text-value--empty' : ''}`}>
                    {dateTo ? formatDate(dateTo) : 'End date'}
                  </span>
                </div>
              </label>
              {(dateFrom || dateTo) && (
                <button className="qh-date-filter__clear" onClick={(e) => { e.stopPropagation(); handleClearDates(); }} title="Clear date filter">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              )}
            </div>

            <button className="qh-export-btn" title="Export — coming soon" disabled>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Export
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="qh-table-wrap">
          {isLoading ? (
            <div className="qh-empty"><p>Loading quotes...</p></div>
          ) : fetchError ? (
            <div className="qh-empty"><p>{fetchError}</p></div>
          ) : paginated.length === 0 ? (
            <div className="qh-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <p>No quotes match your filters</p>
            </div>
          ) : (
            <table className="qh-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Quote ID</th>
                  <th>Effective Date</th>
                  <th>Created Date</th>
                  <th>Vehicles</th>
                  <th>Drivers</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((q: any, i: number) => (
                  <tr key={q.quoteId || `${q.id}-${i}`} className="qh-table__row">
                    <td>
                      <div className="company-cell">
                        <div
                          className="company-cell__avatar"
                          style={{ background: getAvatarColor(q.contact || q.company) }}
                        >
                          {getInitials(q.contact || q.company)}
                        </div>
                        <div className="company-cell__info">
                          <span className="company-cell__name">{q.contact || q.company}</span>
                        </div>
                      </div>
                    </td>
                    <td><span className="quote-id">{q.quoteId}</span></td>
                    <td><span className="qh-date">{q.date ? formatDate(q.date) : '—'}</span></td>
                    <td><span className="qh-date">{q.createdDate ? formatDate(q.createdDate) : '—'}</span></td>
                    <td><span className={q.vehicleCount == null ? 'qh-na' : 'qh-premium'}>{q.vehicleCount ?? 'NA'}</span></td>
                    <td><span className={q.driverCount == null ? 'qh-na' : 'qh-premium'}>{q.driverCount ?? 'NA'}</span></td>
                    <td><span className={badgeClass(q.status)}>{q.status}</span></td>
                    <td>
                      <button
                        className="qh-view-btn"
                        onClick={() => navigate(
                          q.isProspect
                            ? `/quote-history/${q.id}?insuredId=${encodeURIComponent(q.id)}`
                            : `/quote-history/${q.id}`,
                          { state: { quoteId: q.quoteId, status: q.status, company: q.company } })}
                      >
                        View Details
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="5" y1="12" x2="19" y2="12"/>
                          <polyline points="12 5 19 12 12 19"/>
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="qh-footer">
          <span className="qh-footer__info">
            Showing <strong>{paginated.length}</strong> of <strong>{filtered.length}</strong> quotes
          </span>
          <div className="qh-pagination">
            <button className="page-btn" disabled={page === 1} onClick={() => setCurrentPage(p => p - 1)}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </button>
            {(() => {
              const delta = 2;
              const pages: (number | 'ellipsis-start' | 'ellipsis-end')[] = [];
              const rangeStart = Math.max(2, page - delta);
              const rangeEnd   = Math.min(totalPages - 1, page + delta);
              pages.push(1);
              if (rangeStart > 2) pages.push('ellipsis-start');
              for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);
              if (rangeEnd < totalPages - 1) pages.push('ellipsis-end');
              if (totalPages > 1) pages.push(totalPages);
              return pages.map(p =>
                p === 'ellipsis-start' || p === 'ellipsis-end' ? (
                  <span key={p} className="page-btn page-btn--ellipsis">…</span>
                ) : (
                  <button key={p} className={`page-btn${p === page ? ' page-btn--active' : ''}`} onClick={() => setCurrentPage(p)}>
                    {p}
                  </button>
                )
              );
            })()}
            <button className="page-btn" disabled={page === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default QuoteHistory;
